import { Component, ElementRef, HostListener, OnInit, Renderer2, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { UserProvider, Company, LanguageProvider, StatsProvider } from '@lighty';
import { EmitterService, StorageService, TranslateService, ExternalAppsService, ErrorStreamService } from '@services';
import { UrlHelper, DataHelper } from '@helpers';
import * as moment from 'moment';
import { CommonToastService } from '@common2/services/toast.service';
import { CommonModalComponent } from '@common2/components/modal/modal.component';

@Component({
    selector: 'msc-account-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.scss']
})
export class AccountProfileComponent implements OnInit {
    @ViewChild('modalConfirmation') modalConfirmation: CommonModalComponent;
    @ViewChild('upload') upload: ElementRef;
    private accountId: number;
    private privacyUrls: any = {
        fr: 'https://myskillcamp.com/fr/cookies-policy',
        en: 'https://myskillcamp.com/en/cookies-policy',
        nl: 'https://myskillcamp.com/en/cookies-policy',
        it: 'https://myskillcamp.com/en/cookies-policy',
        de: 'https://myskillcamp.com/en/cookies-policy'
    };
    public company: Company;
    public me: any;
    public profile: any;
    public profileEdit: any;
    public editMode: boolean = false;
    public editEnabled: boolean = false;
    public accountIdEdit: number;
    public isSynchronized: boolean;
    public canDoRGPDRequest: boolean;
    public isOpenedNotification: boolean = true;
    public isAcademicBannerCollapsed: boolean = false;
    public activeAcademicField: 'photo' | 'biography' | 'headline' | null = null;

    // ===== Campo combinado "Ciudad y país" (se guarda como un único string en profileEdit.city) =====
    public cityInput: string = '';
    public countries: Array<{ code: string; name: string }> = [];
    public selectedCountry: { code: string; name: string } | null = null;
    public countryDropdownOpen: boolean = false;
    public countrySearch: string = '';

    private savedProfile: any;
    public loadings: any = {
        avatar: false
    };
    public loadingDownload: boolean;
    public selectData: any = {
        interfaceLanguages: []
    }

    // ===== Cambio de contraseña (movido desde settings) =====
    public credentials: any = { old_password: '', password: '', password_confirmation: '' };
    public passwordError: any = { short: false, number: false, uppercase: false, lowercase: false, confirm: false, credential: false, caracters: false };
    public passwordValid: any = { short: false, number: false, uppercase: false, lowercase: false };
    public urlPasswordReset: string | null = null;
    /** Aviso de contraseña actualizada con éxito. */
    public passwordUpdated: boolean = false;
    /** Estado del ojito (mostrar/ocultar) por campo. */
    public showPassword: any = { old: false, new: false, confirm: false };
    /** Evita el autocompletado del navegador: readonly hasta que el usuario enfoca el campo. */
    public oldPassReadonly: boolean = true;

    /** El cambio de contraseña solo aplica al propio perfil, con login por contraseña. */
    get canChangePassword(): boolean {
        return this.me?.id === this.profile?.id && this.company?.mode === 'default' && !this.urlPasswordReset;
    }

    /** true solo si hay foto real subida (no el avatar generado por ui-avatars). */
    get hasUploadedPicture(): boolean {
        return !!(this.profileEdit?.picture && this.profileEdit.picture.indexOf('ui-avatars.com') === -1);
    }

    /** Campos que componen el perfil académico básico (foto, biografía, cargo actual). */
    get academicProfileFields(): Array<{ key: 'photo' | 'biography' | 'headline'; labelKey: string; filled: boolean }> {
        return [
            { key: 'photo', labelKey: 'words.photo', filled: this.hasUploadedPicture },
            { key: 'biography', labelKey: 'words.biography', filled: !!this.profileEdit?.biography },
            { key: 'headline', labelKey: 'account.profile.current-job', filled: !!this.profileEdit?.headline },
        ];
    }

    /** % de avance del perfil académico básico, según los 3 campos anteriores. */
    get academicProfilePercent(): number {
        const total = this.academicProfileFields.length;
        const filled = this.academicProfileFields.filter(f => f.filled).length;
        return total ? Math.round((filled / total) * 100) : 0;
    }

    /** El aviso de perfil académico solo aplica al propio perfil, y desaparece solo al llegar al 100%. */
    get showAcademicBanner(): boolean {
        return this.me?.id === this.profile?.id && this.academicProfilePercent < 100;
    }

    toggleAcademicBanner(): void {
        this.isAcademicBannerCollapsed = !this.isAcademicBannerCollapsed;
        this.storageService.setLocal('academicProfileBannerCollapsed_' + this.me.id, this.isAcademicBannerCollapsed ? '1' : '0');
    }

    goToPublicProfile(): void {
        // academy/public está oculta por el momento: se usa academy/details mientras tanto.
        this.router.navigate(['/learn', this.company.slug, 'academy', 'details']);
    }

    focusAcademicField(fieldKey: 'photo' | 'biography' | 'headline', filled: boolean): void {
        if (filled) {
            return;
        }

        const target = document.getElementById(`academic-field-${fieldKey}`);
        if (!target) {
            return;
        }

        this.activeAcademicField = fieldKey;
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });

        window.setTimeout(() => {
            const focusable = target.querySelector('input, textarea, button') as HTMLElement | null;
            focusable?.focus();
        }, 250);

        window.setTimeout(() => {
            if (this.activeAcademicField === fieldKey) {
                this.activeAcademicField = null;
            }
        }, 2200);
    }

    /** Países filtrados por el texto de búsqueda del dropdown de país. */
    get filteredCountries(): Array<{ code: string; name: string }> {
        const query = (this.countrySearch || '').trim().toLowerCase();
        if (!query) { return this.countries; }
        return this.countries.filter(c => c.name.toLowerCase().includes(query));
    }

    /**
     * Descompone profileEdit.city (ej. "Bogotá, Colombia") en el input de ciudad y el país
     * seleccionado, para precargar el campo combinado al entrar a editar.
     */
    private parseCityCountry(): void {
        const raw = (this.profileEdit?.city || '').trim();
        this.selectedCountry = null;
        this.cityInput = raw;

        if (!raw || !raw.includes(',')) { return; }

        const lastComma = raw.lastIndexOf(',');
        const cityPart = raw.slice(0, lastComma).trim();
        const countryPart = raw.slice(lastComma + 1).trim();
        const match = this.countries.find(c => c.name.toLowerCase() === countryPart.toLowerCase());

        if (match) {
            this.cityInput = cityPart;
            this.selectedCountry = match;
        }
    }

    /** Recombina ciudad + país en un único string y lo guarda en profileEdit.city. */
    updateCombinedCity(): void {
        const city = (this.cityInput || '').trim();
        const country = this.selectedCountry?.name || '';

        if (city && country) {
            this.profileEdit.city = `${city}, ${country}`;
        } else {
            this.profileEdit.city = city || country;
        }
    }

    /** Al vaciar por completo el campo de ciudad, se resetea también el país elegido. */
    onCityInputChange(): void {
        if (!(this.cityInput || '').trim()) {
            this.selectedCountry = null;
        }
        this.updateCombinedCity();
    }

    toggleCountryDropdown(event: Event): void {
        event.stopPropagation();
        if (this.isSynchronized || !this.editEnabled) { return; }
        this.countryDropdownOpen = !this.countryDropdownOpen;
        this.countrySearch = '';
    }

    selectCountry(country: { code: string; name: string }): void {
        this.selectedCountry = country;
        this.countryDropdownOpen = false;
        this.updateCombinedCity();
    }

    @HostListener('document:click')
    closeCountryDropdown(): void {
        this.countryDropdownOpen = false;
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private userProvider: UserProvider,
        private toastService: CommonToastService,
        private renderer: Renderer2,
        private emitterService: EmitterService,
        private storageService: StorageService,
        private translateService: TranslateService,
        private externalAppsService: ExternalAppsService,
        private languageProvider: LanguageProvider,
        private errorStreamService: ErrorStreamService,
        private statsProvider: StatsProvider,
    ) { }

    ngOnInit(): void {
        this.me = this.storageService.get('me');
        this.company = this.storageService.get('company');
        this.profile = this.route.snapshot.data.profile || this.route.parent.snapshot.data.user;

        this.isSynchronized = this.profile.companyUser.synchronized;
        this.canDoRGPDRequest = !this.profile.companyRoles.companyAdministrator && !this.profile.companyRoles.companyOwner && this.company.experience.contactId !== null;

        this.savedProfile = DataHelper.clone(this.profile);

        // Vista unificada: siempre editable (se inicializa la copia de edición al cargar).
        this.profileEdit = DataHelper.clone(this.profile, {}, true);
        this.editMode = true;

        // Countries solo tiene diccionario en fr/en/it/nl/de: cae a 'en' para cualquier otro idioma (ej. 'es').
        const supportedCountryLangs = ['fr', 'en', 'it', 'nl', 'de'];
        const countryLang = this.translateService.getCurrentLang();
        this.countries = DataHelper.getCountries(supportedCountryLangs.includes(countryLang) ? countryLang : 'en');
        this.parseCityCountry();

        if (this.profile.id === this.me.id) {
            this.editEnabled = true;
        } else {
            this.accountId = this.profile.id;
            if (this.storageService.get('access').roles.companyOwner) {
                this.editEnabled = true;
            }
        }

        if (this.storageService.get('editProfile')) {
            this.accountIdEdit = this.storageService.getFlash('editProfile');
            this.edit(true);
        }

        this.urlPasswordReset = (JSON.parse(localStorage.getItem('theme') || '{}'))?.urlPasswordReset || null;

        this.isAcademicBannerCollapsed = this.storageService.getLocal('academicProfileBannerCollapsed_' + this.me.id) === '1';

        this.getLanguages();
    }

    checkPasswordMatch(): void {
        // Al reeditar, oculta el aviso de éxito previo.
        this.passwordUpdated = false;
        const regNumber = RegExp(/^.*(?=.*[\d\x]).*$/);
        const regUppercase = RegExp(/^.*(?=.*[A-Z]).*$/);
        const regLowercase = RegExp(/^.*(?=.*[a-z]).*$/);

        this.passwordError.confirm = this.credentials.password !== this.credentials.password_confirmation;
        this.passwordError.short = this.credentials.password.length > 0 && this.credentials.password.length < 8;
        this.passwordError.number = !regNumber.test(this.credentials.password);
        this.passwordError.uppercase = !regUppercase.test(this.credentials.password);
        this.passwordError.lowercase = !regLowercase.test(this.credentials.password);

        this.passwordValid.short = this.credentials.password.length >= 8;
        this.passwordValid.number = regNumber.test(this.credentials.password) && this.credentials.password.length > 0;
        this.passwordValid.uppercase = regUppercase.test(this.credentials.password) && this.credentials.password.length > 0;
        this.passwordValid.lowercase = regLowercase.test(this.credentials.password) && this.credentials.password.length > 0;
    }

    savePassword(): void {
        this.errorStreamService.locked();
        const params: any = {
            old_password: this.credentials.old_password,
            password: this.credentials.password,
        };
        this.userProvider.updatePassword(params).subscribe(() => {
            this.toastService.onSuccess(this.translateService.instant('toast.password-updated'));
            this.errorStreamService.unlocked();
            this.passwordError = { short: false, caracters: false, confirm: false, credential: false };
            this.passwordValid = { short: false, number: false, uppercase: false, lowercase: false };
            this.credentials = { old_password: '', password: '', password_confirmation: '' };
            this.showPassword = { old: false, new: false, confirm: false };
            this.passwordUpdated = true;
        }, (failure) => {
            this.toastService.onError(this.translateService.instant('toast.error-occurred'));
            if (failure.error.message.password) {
                for (const message of failure.error.message.password) {
                    switch (message) {
                        case 'too-short': this.passwordError.short = true; break;
                        case 'num-maj-min': this.passwordError.caracters = true; break;
                    }
                }
            } else if (failure.error.message) {
                if (failure.error.message === 'wrong credentials') { this.passwordError.credential = true; }
            }
            this.errorStreamService.unlocked();
        });
    }

    getLanguages() {
        this.languageProvider.get('interface').subscribe(resp => {
            this.selectData.interfaceLanguages = this.getSelectData(resp);
        })
    }

    getSelectData(data) {
        return data.map(d => ({
            id: d.id,
            label: d.name,
            value: d,
            isSelected: d.id === this.profile?.language.id
        }));
    }

    onSelectOption(option) {
        this.selectData.interfaceLanguages = this.selectData.interfaceLanguages.map(d => {
            d.isSelected = d.id === option.id;
            return d;
        });

        this.onSelect(option.value);
    }

    edit(editable: boolean): void {
        if (editable) {
            this.profileEdit = DataHelper.clone(this.profile, {}, true);
            this.parseCityCountry();
        }
        this.editMode = editable;
    }

    deletePicture(): void {
        this.loadings.avatar = true;

        const bgColors = ['fdad92', 'FFD796', 'FC5752', 'fb97ef', 'cb96ff', '98FF96', '5FACFF', '36E0E8'];
        const textColors = ['f2615f', 'f38c59', 'A73936', 'e04acb', 'a35ff0', '3fd78c', '2264AB', '1E8388'];
        const colorIndex = this.profileEdit.id % 7;
        this.profileEdit.picture = `https://ui-avatars.com/api/?background=${bgColors[colorIndex]}&color=${textColors[colorIndex]}&font-size=0.6&length=1&name=${this.profileEdit.firstname}+${this.profileEdit.lastname}?size=64`;
        this.profileEdit.mediaId = null;
        this.loadings.avatar = false;
    }

    uploadPicture(): void {
        const event = new MouseEvent('click', { bubbles: true });
        this.renderer.selectRootElement(this.upload.nativeElement).dispatchEvent(event);
    }

    updatePicture(event: any): void {
        this.loadings.avatar = true;

        this.upload.nativeElement.value = null;

        if (event.type === 'finished') {
            this.profileEdit.picture = event.value.pictureUrl;
            this.profileEdit.mediaId = event.value.id;
            this.loadings.avatar = false;
        }

        if (event.type === 'error') {
            this.loadings.avatar = false;
            this.toastService.onError(this.translateService.instant('toast.error-occurred'));
        }
    }

    cancel(): void {
        // Descarta los cambios locales volviendo a la última copia guardada (sin llamar al servidor).
        this.profileEdit = DataHelper.clone(this.savedProfile, {}, true);
        this.parseCityCountry();
    }

    save(forceSave?): void {
        if (!forceSave && this.isActiveSSOChanged()) {
            this.modalConfirmation.onOpen();
            return;
        }

        this.userProvider.updateAccount(this.profileEdit, this.company.id, this.accountId).subscribe((data) => {
            this.toastService.onSuccess(this.translateService.instant('toast.saved'));
            this.profile = data;
            this.savedProfile = DataHelper.clone(data);
            // Se mantiene la vista editable con los datos recién guardados.
            this.profileEdit = DataHelper.clone(data, {}, true);
            this.parseCityCountry();
            this.refreshMe();
        });
    }

    isActiveSSOChanged(): boolean {
        return this.savedProfile.companyUser.hasSSO && this.profileEdit.companyUser.externalId !== this.savedProfile.companyUser.externalId
    }

    requestUpdate(contact: any): void {
        const slug = this.company.experience.contact ? this.company.experience.contact.conversationId : null;
        this.storageService.set('conversationUsers', [contact]);
        this.router.navigate(['conversation', slug || 'create']);
    }

    refreshMe(): void {
        this.emitterService.refreshMe.emit(true);
    }

    privacyPolicy(): void {
        UrlHelper.navigate(this.privacyUrls[this.translateService.getCurrentLang()]);
    }

    manageAccess(): void {
        this.router.navigate(['../access', 'learn'], { relativeTo: this.route });
    }

    getTranslateDate(): string {
        const today = moment().format('YYYY-MM-DD');
        const subscriptionDate = moment(this.profile.companyUser.subscriptionDate).format('YYYY-MM-DD');
        const startDate = moment(this.profile.companyUser.accessStatus.accessStartAt).format('YYYY-MM-DD');

        if (today >= subscriptionDate && (!this.profile.companyUser.accessStatus.accessStartAt || startDate <= today)) {
            return 'words.since-the';
        }
        if (this.profile.companyUser.accessStatus.accessStartAt && today < startDate) {
            return 'words.from-the';
        }

        return null;
    }

    onSelect(data: any) {
        this.profileEdit.languageId = data.id;
    }

    download(ev, accountId): void {
        this.loadingDownload = true;
        const params = {
            type: ev.type,
            format: ev.format,
            emails: [this.storageService.get('me').email],
            accountId
        };

        this.statsProvider.getExport(this.company.id, params).subscribe({
            next: (data) => {
                this.toastService.onSuccess(this.translateService.instant('toast.send-file-mail'));
                this.errorStreamService.unlocked();
                this.loadingDownload = false;
            },
            error: () => {
                this.toastService.onError(this.translateService.instant('toast.error-occurred'));
                this.errorStreamService.unlocked();
                this.loadingDownload = false;
            }
        });
    }

    downloadHistoryWrapper() {
        this.download({ format: 'xlsx', type: 'learner-personal-history' }, this.me.id !== this.profile?.id ? this.profile.id : this.me.id);
    }
}
